import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FileExtension } from 'src/app/models/product-data-filename-conventions/file-extension';
import {
  FileExtension as FileExtensionLiteral,
  FilenameIdentifier,
} from 'src/app/models/product-data-filename-conventions/product-data-filename-convention';
import { ReviewProductDataFilesDialogComponent } from '../../review-product-data-files-dialog/review-product-data-files-dialog.component';
import { ReviewProductDataFilesDialogData } from 'src/app/models/outputs-collections/review-product-data-files/dialog-data';
import { ReviewProductDataFilenamesDialogComponent } from '../../review-product-data-filenames-dialog/review-product-data-filenames-dialog.component';
import { ReviewProductDataFilenamesDialogData } from 'src/app/models/outputs-collections/review-product-data-filenames/dialog-data';
import { ProductDataFilenameConventionSummary } from 'src/app/models/outputs-collections/review-product-data-filenames/product-data-filename-convention';
import { OutputsCollectionsService } from 'src/app/services/outputs-collections.service';
import { CollectionAssetPreviewResult } from 'src/app/models/outputs-collections/outputs-collections-result';
import { buildFormatConvention } from 'src/app/utils/format-convention';

export interface ProductDataFilesBlockers {
  missing: number;
  pdfcMissing: boolean;
  dataConventionMissing: boolean;
}

@Component({
  selector: 'app-collection-product-data-files-selection',
  templateUrl: './collection-product-data-files-selection.component.html',
  styleUrls: ['./collection-product-data-files-selection.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class CollectionProductDataFilesSelectionComponent implements OnInit {
  @Input() formatsGeneratedInStep3: FileExtension[] = [];
  @Input() pdfcConvention: ProductDataFilenameConventionSummary | null = null;
  @Input() filenameIdentifier: FilenameIdentifier | null = null;
  @Input() productIds: string[] = [];
  @Input() organizationId = '';
  @Input() dataConventionConfigured = false;
  @Output() readonly selectionChange = new EventEmitter<FileExtension[]>();
  @Output() readonly blockersChange = new EventEmitter<ProductDataFilesBlockers | null>();

  includeProductData = false;
  selectedFormats: FileExtension[] = [];
  blockers: ProductDataFilesBlockers | null = null;
  isLoadingPreview = false;

  constructor(
    private readonly dialog: MatDialog,
    private readonly outputsCollectionsService: OutputsCollectionsService,
  ) {}

  ngOnInit(): void {
    this.emitSelection();
  }

  toggleIncludeProductData(include: boolean): void {
    this.includeProductData = include;
    this.selectedFormats = include ? [...this.formatsGeneratedInStep3] : [];
    this.emitSelection();
    this.refreshBlockers();
  }

  toggleAllFormats(checked: boolean): void {
    this.selectedFormats = checked ? [...this.formatsGeneratedInStep3] : [];
    this.emitSelection();
    this.refreshBlockers();
  }

  toggleFormat(format: FileExtension, checked: boolean): void {
    if (checked) {
      if (!this.selectedFormats.includes(format)) {
        this.selectedFormats = [...this.selectedFormats, format];
      }
    } else {
      this.selectedFormats = this.selectedFormats.filter((entry) => entry !== format);
    }
    this.emitSelection();
    this.refreshBlockers();
  }

  get hasBlockers(): boolean {
    return this.blockers != null && this.blockers.missing > 0;
  }

  isFormatSelected(format: FileExtension): boolean {
    return this.selectedFormats.includes(format);
  }

  formatConvention(format: FileExtension): string {
    if (this.filenameIdentifier == null) return '';
    return buildFormatConvention(this.filenameIdentifier, format as unknown as FileExtensionLiteral);
  }

  get noPdfcConfigured(): boolean {
    return this.formatsGeneratedInStep3.length === 0;
  }

  get orgUpdateLink(): string {
    return `/organizations/${this.organizationId}/update`;
  }

  openReviewFiles(): void {
    const data: ReviewProductDataFilesDialogData = {
      scope: {
        productIds: this.productIds,
        organizationId: this.organizationId,
      },
      productDataFormats: [...this.selectedFormats],
    };
    this.dialog.open(ReviewProductDataFilesDialogComponent, {
      width: '800px',
      data,
    });
  }

  openReviewFilenames(): void {
    if (this.pdfcConvention == null) return;
    const data: ReviewProductDataFilenamesDialogData = {
      scope: {
        productIds: this.productIds,
        organizationId: this.organizationId,
      },
      productDataFormats: [...this.selectedFormats],
      convention: this.pdfcConvention,
    };
    this.dialog.open(ReviewProductDataFilenamesDialogComponent, {
      width: '800px',
      data,
    });
  }

  private emitSelection(): void {
    this.selectionChange.emit(this.includeProductData ? [...this.selectedFormats] : []);
  }

  private refreshBlockers(): void {
    if (!this.includeProductData) {
      this.blockers = null;
      queueMicrotask(() => this.blockersChange.emit(null));
      return;
    }
    const pdfcMissing = this.noPdfcConfigured;
    const dataConventionMissing = !this.dataConventionConfigured;
    if (pdfcMissing || dataConventionMissing || this.selectedFormats.length === 0) {
      this.blockers = { missing: 0, pdfcMissing, dataConventionMissing };
      queueMicrotask(() => this.blockersChange.emit(this.blockers));
      return;
    }
    this.isLoadingPreview = true;
    this.outputsCollectionsService
      .previewOutputsCollection({
        productIds: this.productIds,
        outputFormats: [],
        productDataFormats: [...this.selectedFormats],
      })
      .subscribe({
        next: (response: CollectionAssetPreviewResult) => {
          this.blockers = this.mapToBlockers(response);
          this.blockersChange.emit(this.blockers);
          this.isLoadingPreview = false;
        },
        error: () => {
          this.blockers = null;
          this.blockersChange.emit(null);
          this.isLoadingPreview = false;
        },
      });
  }

  private mapToBlockers(response: CollectionAssetPreviewResult): ProductDataFilesBlockers {
    const productDataRows = response.assets.filter((asset) => asset.isProductData);
    const missing = productDataRows.filter((row) => !row.fileExists).length;
    return { missing, pdfcMissing: false, dataConventionMissing: false };
  }
}
